import { TermScheduleFlags } from '../../views/course_scheduler/services/term_schedule_flags';
import { ICommand } from 'core-ts-types';
import * as _4 from 'lodash4';
import { ISection, ISectionType, ILocation, TMenuItems, IMenuSettings } from '../../interfaces/scheduler_interfaces';
import { UnscheduledSectionServant } from '../../views/unscheduled_sections/servants/unscheduled_section_servant';
import { OpenModalFlag } from '../../views/course_scheduler/components/calendar_placeholder_alert/openModalFlag';
import { ScheduledSectionServant } from '../../views/course_scheduler/servants/scheduled_section_servant';
import { SectionSwapServant } from '../../swap/servants/section_swap_servant';
import { SwapFacade } from '../../swap/facades/swap_facade';
import { SectionActionStrategy } from './strategies/section_action_strategy';
import { ISchedulerConfigModel } from '../../config/models/scheduler_config_model';
import { ScheduleSectionFacade } from '../../views/registration/facades/schedule_section_facade';
import { CommunicatorService } from 'ui-ng-component-communicator';
import { Strategy } from 'utility-ts-strategy';
import { RefreshSeatAvailabilityFacade } from '../../views/course_scheduler/facades/refresh_seat_availability_facade';

export class SectionActionButtonController {
  private section: ISection;
  private canAddToWaitlist: boolean;
  private canDrag: boolean;
  private isConflictWithTimeOrCampus: boolean;
  private showSeparator: boolean;
  private uniqId: string = '';
  private canSwap: boolean;
  private sectionLabel: string;
  private sectionTitle: string = '';
  private sectionActionMenuItems: TMenuItems;
  private sectionActionMenuSettings: IMenuSettings;

  constructor(
    private $stateParams: ng.ui.IStateParamsService,
    private getCrossCourseLinkedSectionsCommand: ICommand,
    private getSectionDetailsCommand: ICommand,
    private $state: ng.ui.IStateService,
    private clearCrossCourseLinkedSectionsCommand: ICommand,
    private getTermPinCommand: ICommand,
    private ngOpenModalFlag: OpenModalFlag,
    private removeSelectedUnscheduledSectionCommand: ICommand,
    private loadStudentUnavailabilityCommand: ICommand,
    private ngUnscheduledSectionServant: UnscheduledSectionServant,
    private ngScheduledSectionServant: ScheduledSectionServant,
    private ngSectionSwapServant: SectionSwapServant,
    private schedulerConfigModel: ISchedulerConfigModel,
    private FEATURE_SECTION_LABEL_NK: string,
    private ngScheduleSectionFacade: ScheduleSectionFacade,
    private ngSwapFacade: SwapFacade,
    private calendarPreviewRemoveSections: any,
    private ngTermScheduleFlags: TermScheduleFlags,
    private ngComponentCommunicatorService: CommunicatorService,
    private ngRefreshSeatAvailabilityFacade: RefreshSeatAvailabilityFacade
  ) {}
  $onInit() {
    this.uniqId = _4.uniqueId();
    this.updateSectionFlags();
  }

  $onChanges() {
    this.updateSectionFlags();
  }

  updateSectionFlags() {
    this.canDrag = this.ngUnscheduledSectionServant.canDragSection(this.section);
    this.canAddToWaitlist = this.ngUnscheduledSectionServant.canAddToWaitlist(this.section);
    this.showSeparator = this.canDrag || this.canAddToWaitlist;
    this.isConflictWithTimeOrCampus =
      this.ngUnscheduledSectionServant.isSectionConflictingWithBusyTimes(this.section) ||
      this.ngUnscheduledSectionServant.isSectionConflictingWithCampusPreferences(this.section);
    this.canSwap = this.ngSectionSwapServant.isInSwapMode();
    this.buildMenuItems();
    this.sectionLabel = this.schedulerConfigModel.getConfigProperty(this.FEATURE_SECTION_LABEL_NK);
    this.sectionTitle = `${_4.get(this.section, '_course.cd', '')} - ${this.sectionLabel} ${_4.get(
      this.section,
      'cd',
      ''
    )}`;
  }

  isSectionTypeScheduled(sectionType: ISectionType) {
    return this.ngScheduledSectionServant.isSectionTypeScheduled(sectionType);
  }

  unsetModalFlag() {
    this.ngOpenModalFlag.setFlag(false);
    this.clearCrossCourseLinkedSectionsCommand();
  }

  addPressed() {
    this.removeSelectedUnscheduledSectionCommand();
    this.updateSectionFlags();
    if (this.isConflictWithTimeOrCampus) {
      this.loadStudentUnavailabilityCommand(this.$stateParams.termId);
      this.getSectionDetailsCommand(this.section.id).then(() =>
        this.ngComponentCommunicatorService.publish('OPEN_CONFLICT_PROMPT')
      );
    } else {
      this.scheduleSection();
    }
    this.calendarPreviewRemoveSections();
  }

  addToWaitlist() {
    this.getTermPinCommand(this.$stateParams.termId).then(() => {
      this.getSectionDetailsCommand(this.section.id).then(() => {
        this.ngComponentCommunicatorService.publish('OPEN_WAITLIST_PROMPT');
      });
    });
  }

  isFullSection() {
    return this.ngUnscheduledSectionServant.isSectionFull(this.section);
  }

  isFullSectionAllowedToMove() {
    return this.ngTermScheduleFlags.isFullSectionsSchedulingEnabled() && this.isFullSection();
  }

  showAddSection() {
    if (this.canDrag && !this.canAddToWaitlist && !this.canSwap) {
      return true;
    }
    return this.isFullSectionAllowedToMove();
  }

  showAddToWaitlist() {
    if (this.canAddToWaitlist) {
      return true;
    }
    if (this.isFullSectionAllowedToMove()) {
      return (
        this.ngTermScheduleFlags.isWaitlistEnabled() &&
        !this.ngUnscheduledSectionServant.isWaitlisted(this.section) &&
        this.ngUnscheduledSectionServant.hasWaitlist(this.section) &&
        !this.ngUnscheduledSectionServant.isWaitlistFull(this.section)
      );
    }
    return false;
  }

  buildMenuItems() {
    this.sectionActionMenuSettings = {
      subtitle: this.sectionTitle,
      capHeight: true
    };
    this.sectionActionMenuItems = [
      {
        value: 'refresh_seat_availability',
        title: 'Refresh Seat Availability',
        visible: true
      },
      {
        value: SectionActionStrategy['VIEW_DETAILS'].action,
        title: SectionActionStrategy['VIEW_DETAILS'].title,
        visible: true
      },
      {
        value: SectionActionStrategy['ADD_SECTION'].action,
        title: SectionActionStrategy['ADD_SECTION'].title,
        visible: this.showAddSection()
      },
      {
        value: SectionActionStrategy['ADD_TO_WAITLIST'].action,
        title: SectionActionStrategy['ADD_TO_WAITLIST'].title,
        visible: this.showAddToWaitlist()
      },
      {
        value: SectionActionStrategy['SWAP_SECTION'].action,
        title: SectionActionStrategy['SWAP_SECTION'].title,
        visible: this.canSwap
      }
    ].filter((item) => item.visible);
  }

  private readonly __menuItemStrategy = new Strategy({
    refresh_seat_availability: () => this.ngRefreshSeatAvailabilityFacade.refreshSeats(this.section.id),
    view_section_details: () => this.openSectionDetail(),
    schedule_section: () => this.addPressed(),
    add_to_waitlist: () => this.addToWaitlist(),
    swap_section: () => this.swap()
  });

  menuItemSelected($event: CustomEvent) {
    const invokeMenuStrategy = this.__menuItemStrategy.get($event.detail.value);
    invokeMenuStrategy();
  }

  swap() {
    this.ngSwapFacade.saveSwapToAndRedirect(this.section);
  }

  getCrossCourseLinkedSections() {
    if (_4.get(this.section, '_availability.has_linked_section_coreqs', false)) {
      this.getCrossCourseLinkedSectionsCommand(this.section.id);
    }
  }

  openSectionDetail() {
    this.getSectionDetailsCommand(this.section.id).then(() =>
      this.ngComponentCommunicatorService.publish('OPEN_SECTION_DETAIL_MODAL')
    );
  }

  scheduleSection() {
    if (this.isConflictWithTimeOrCampus) {
      this.getSectionDetailsCommand(this.section.id).then(() =>
        this.ngComponentCommunicatorService.publish('OPEN_CONFLICT_PROMPT')
      );
    } else {
      this.completeSectionMovement();
    }
  }
  completeSectionMovement() {
    this.ngScheduleSectionFacade.scheduleSection(this.section);
  }

  private sectionLocations(section: ISection): ILocation[] {
    const locations = _4.uniq(_4.map(section._section_times, (sectionTime) => sectionTime._location));
    return locations;
  }
}

angular.module('capability-ng-scheduler').component('sectionActionButton', {
  templateUrl: 'capability-ng-scheduler/src/components/section_action_button/section_action_button.html',
  controller: SectionActionButtonController,
  bindings: {
    section: '<'
  }
});



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/components/section_action_button/section_action_button.ts